import SocketDownload from "./SocketDownload";
import SocketUpload from "./SocketUpload";
import * as ConfigStepMscore from "../../../../configuration/config/ConfigStepMscore";
import Logger from "../../../../utils/Logger";
import DateUtil from "../../../../utils/DateUtil";
import EQmScoreStepProgress from "../../../../../public-apis/onclick/EQmScoreStepProgress";
import * as KpiMapper from "../../../../gateway/service/modules/kpi/mapper/KpiMapper";
const RAW_DATA_PERIOD_MS = 500;

/**
 * Manage all sockets
 */
export default class SocketManager {

    /**
     * Default constructor
     * @param {GpsManager} gpsManager to get location with raw data
     * @param {UrlBuilder} urlBuilder builder to format URL
     * @param {number} nbSockets the count of sockets to run
     * @param {string} direction the direction of the socket (dl/ul)
     * @param {number} timeout the timeout in ms
     * @param {GatewayService} gatewayService the gateway service
     */
    constructor(gpsManager, urlBuilder, nbSockets, direction, timeout, gatewayService) {
        this.gpsManager = gpsManager;
        this.urlBuilder = urlBuilder;
        this.socketCount = nbSockets;
        this.direction = direction;
        this.timeout = timeout;
        this.sockets = [];
        this.raw = [];
        this.isTimeout = false;
        this.gatewayService = gatewayService;
        this.gatewayInfos = null;
    }

    /**
     * Start all sockets
     * @param {function} progress progress callback
     * @param {string} url to start test
     * @param {number} volume the volume to exchange (only for UL)
     * @param {string} mScoreServerUrl the mscore url server
     * @return {Promise}
     */
    start(progress, url, volume, mScoreServerUrl) {
        Logger.log("Start test");
        let data = null;
        // If upload, generate data for all sockets
        if (this.direction == ConfigStepMscore.STEP_DIRECTION_UP) {
            let start = DateUtil.getCurrentTimestampMs();
            data = new Uint8Array(new ArrayBuffer(volume));
            Logger.log("Generate the file of " + volume + " bytes in " + (DateUtil.getCurrentTimestampMs() - start));
        }
        const startDate = DateUtil.getCurrentTimestampMs();
        const collector = this.startCollectRaw(progress, startDate, volume);

        let socketInError = 0;
        return new Promise((resolve, reject) => {
            Logger.log("Call " + url);
            setTimeout((self) => {
                Logger.log("Timeout reached!");
                self.stop();
            }, this.timeout, this);
            Promise.all(this.initSockets(url, data)).then(() => {
                Logger.log("All sockets are done");
                resolve({results: this.stopCollectorRaw(progress, collector, startDate), mScoreServerUrl: mScoreServerUrl});
            }).catch((error) => {
                socketInError++;
                Logger.log("Socket in error: " + socketInError);
                reject(error);
            });
        });
    }

    /**
     * Abort all sockets
     * @return {boolean} always true
     */
    stop() {
        this.sockets.forEach((socket) => {
            socket.stop();
        });
        return true;
    }

    /**
     * Initialize a pool of Socket
     * @param {string} url the url to call
     * @param {Uint8Array | null} data
     * @return {Array.<Socket>}
     */
    initSockets(url, data = null) {
        const promises = [];
        for (let i = 0; i < this.socketCount; i++) {
            const randomUrl = url + '&random=' + Math.random();
            let socket = null;
            if (this.direction === ConfigStepMscore.STEP_DIRECTION_DL) {
                socket = new SocketDownload(randomUrl);
            } else {
                socket = new SocketUpload(randomUrl, data);
            }
            this.sockets.push(socket);
            promises.push(socket.run());
        }

        return promises;
    }

    /**
     * @return {number} the bytes exchanged by all sockets
     */
    getTotalBytesExchanged() {
        let volume = 0;
        this.sockets.forEach((socket) => {
            volume += socket.volume;
        });
        return volume;
    }

    /**
     *
     * Start the raw data collect
     *
     * @param {function} progress the progress callback to alert each time we collect raw data
     * @param {number} startTime the timestamp when test started
     * @param {number} targetVolume the volume to download/upload kill socket if volume raised
     * @return {setInterval}
     */
    startCollectRaw(progress, startTime, targetVolume) {
        this.lastDate = DateUtil.getCurrentTimestampMs();
        this.lastBytes = 0;
        // Init a first raw data value
        const rawData = this.collectRaw(this.getTotalBytesExchanged(), startTime, this.lastDate);
        this.gatewayService.startCollect((err, kpi) => {
            if (!err) {
                this.gatewayInfos = kpi;
            } else {
                Logger.log(err.message);
                if (this.gatewayService.getCacheInfos()) {
                    this.gatewayInfos = this.gatewayService.getCacheInfos()[KpiMapper.RAW];
                } else {
                    this.gatewayInfos = null;
                }
            }
        }, RAW_DATA_PERIOD_MS);
        this.raw.push(rawData);
        // Collect raw data at interval
        return setInterval(() => {
            const currentTime = DateUtil.getCurrentTimestampMs();
            const rawData = this.collectRaw(this.getTotalBytesExchanged(), startTime, currentTime);
            progress(rawData);
            this.raw.push(rawData);
            if (rawData.totalBytes > targetVolume) {
                this.stop();
            }
        }, RAW_DATA_PERIOD_MS);
    }

    /**
     *
     * @param {function} progress
     * @param {setInterval} collector
     * @param {number} startTime start time
     * @return {Array.<EQmScoreStepProgress>}
     */
    stopCollectorRaw(progress, collector, startTime) {
        clearInterval(collector);
        const rawData = this.collectRaw(this.getTotalBytesExchanged(), startTime, DateUtil.getCurrentTimestampMs());
        this.gatewayService.stopCollect();
        this.gatewayInfos = null;
        progress(rawData);
        this.raw.push(rawData);
        return this.raw;
    }

    /**
     * Collect a raw data
     *
     * @param {number} totalBytes total bytes exchanged since the beginning
     * @param {number} startTime the timestamp when test started
     * @param {number} currentTime the current timestamp
     * @return {EQmScoreStepProgress} the raw data collected
     */
    collectRaw(totalBytes, startTime, currentTime) {
        const lastBytes = this.lastBytes;
        this.lastBytes = totalBytes;
        const lastDate = this.lastDate;
        this.lastDate = currentTime;

        return new EQmScoreStepProgress(this.direction,
            currentTime,
            totalBytes - lastBytes,
            currentTime - lastDate,
            totalBytes,
            currentTime - startTime,
            this.gpsManager.get(),
            this.timeout,
            this.gatewayInfos
            );
    }


}



// WEBPACK FOOTER //
// ./js/core/script/executors/mscore/socket/SocketManager.js